import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getCompanyPaymentMethods, addPaymentMethod } from '../services/paymentService';
import { Box, Button, Heading, Text, VStack, Flex, Divider } from '@chakra-ui/react';

const CARD_OPTIONS = {
  style: {
    base: {
      color: '#1e293b',
      fontFamily: '"Poppins", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444'
    }
  }
};

const PaymentMethodManager = ({ companyId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [addingNew, setAddingNew] = useState(false);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const methods = await getCompanyPaymentMethods(companyId);
        setPaymentMethods(methods);
        setError(null);
      } catch (err) {
        setError('Failed to load payment methods');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (companyId) {
      fetchPaymentMethods();
    }
  }, [companyId]);

  const handleAddPaymentMethod = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setError(`Failed to add payment method: ${error.message}`);
      } else {
        // Save the payment method to our database
        const paymentMethodData = {
          companyId,
          stripePaymentMethodId: paymentMethod.id,
          type: paymentMethod.type,
          card: {
            brand: paymentMethod.card.brand,
            last4: paymentMethod.card.last4,
            expiryMonth: paymentMethod.card.exp_month,
            expiryYear: paymentMethod.card.exp_year
          },
          isDefault: paymentMethods.length === 0 // Make it default if it's the first one
        };

        const savedMethod = await addPaymentMethod(paymentMethodData);
        setPaymentMethods([...paymentMethods, savedMethod]);
        setAddingNew(false);
        cardElement.clear();
      }
    } catch (err) {
      setError(`Failed to add payment method: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const getCardIcon = (brand) => {
    const cardIcon = (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    );
    
    switch (brand.toLowerCase()) {
      case 'visa':
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {cardIcon} Visa
          </span>
        );
      case 'mastercard':
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {cardIcon} Mastercard
          </span>
        );
      case 'amex':
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {cardIcon} American Express
          </span>
        );
      case 'discover':
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {cardIcon} Discover
          </span>
        );
      default:
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {cardIcon} Card
          </span>
        );
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Text fontSize="lg" color="#6c63ff">Loading payment methods...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading as="h3" size="md" mb={6} color="#6c63ff">
        Your Payment Methods
      </Heading>

      {error && (
        <Box 
          p={4} 
          mb={5} 
          borderRadius="8px" 
          bg="rgba(239, 68, 68, 0.1)" 
          color="#ef4444"
        >
          <Text>{error}</Text>
        </Box>
      )}

      {paymentMethods.length > 0 ? (
        <VStack spacing={4} align="stretch" mb={6}>
          {paymentMethods.map((method) => (
            <Box 
              key={method.id} 
              p={5} 
              borderRadius="12px" 
              boxShadow="0 4px 12px rgba(0,0,0,0.05)"
              bg="white"
              transition="all 0.3s ease"
              _hover={{
                transform: "translateY(-3px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)"
              }}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Box>
                  <Text fontSize="lg" fontWeight="bold">
                    {getCardIcon(method.card.brand)}
                  </Text>
                  <Text color="#666" mt={1}>
                    •••• •••• •••• {method.card.last4}
                  </Text>
                  <Text fontSize="sm" color="#888" mt={1}>
                    Expires {method.card.expiryMonth}/{method.card.expiryYear}
                  </Text>
                </Box>
                {method.isDefault && (
                  <Box 
                    px={3} 
                    py={1} 
                    borderRadius="full" 
                    bg="rgba(34, 197, 94, 0.1)"
                    color="#22c55e"
                    fontSize="sm"
                    fontWeight="500"
                  >
                    Default
                  </Box>
                )}
              </Flex>
            </Box>
          ))}
        </VStack>
      ) : !addingNew ? (
        <Box 
          p={6} 
          borderRadius="12px" 
          bg="#f5f7ff" 
          textAlign="center"
          mb={6}
        >
          <Text fontSize="lg" mb={2}>No payment methods added yet</Text>
          <Text color="#666" mb={4}>Add a payment method to start making payments</Text>
        </Box>
      ) : null}

      {/* Add new payment method form */}
      {addingNew ? (
        <Box 
          p={6} 
          borderRadius="12px" 
          borderWidth="2px" 
          borderColor="#eef1ff" 
          bg="white"
          boxShadow="0 4px 12px rgba(0,0,0,0.05)"
          mb={6}
        >
          <Heading as="h4" size="sm" mb={4} color="#6c63ff">
            Add New Payment Method
          </Heading>
          
          <form onSubmit={handleAddPaymentMethod}>
            <Box mb={5}>
              <Text mb={2} fontWeight="500">Card Details</Text>
              <Box 
                p={4} 
                borderWidth="2px" 
                borderRadius="8px" 
                borderColor="#eef1ff"
                bg="#f8fafc"
                _hover={{
                  borderColor: "#6c63ff"
                }}
              >
                <CardElement options={CARD_OPTIONS} />
              </Box>
            </Box>

            <Flex gap={3}>
              <Button 
                type="submit" 
                isDisabled={processing || !stripe}
                bgGradient="linear(to-r, #6c63ff, #3b82f6)"
                color="white"
                borderRadius="50px"
                boxShadow="0 4px 15px rgba(108, 99, 255, 0.3)"
                _hover={{
                  bgGradient: "linear(to-r, #6c63ff, #3b82f6)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(108, 99, 255, 0.4)"
                }}
                transition="all 0.3s ease"
              >
                {processing ? 'Processing...' : 'Save Card'}
              </Button>
              <Button 
                onClick={() => setAddingNew(false)}
                variant="outline"
                borderWidth="2px"
                borderColor="#6c63ff"
                color="#6c63ff"
                borderRadius="50px"
                _hover={{
                  bg: "rgba(108, 99, 255, 0.1)",
                  transform: "translateY(-2px)"
                }}
                transition="all 0.3s ease"
              >
                Cancel
              </Button>
            </Flex>
          </form>
        </Box>
      ) : (
        <Button 
          onClick={() => setAddingNew(true)}
          variant="outline"
          borderWidth="2px"
          borderColor="#6c63ff"
          color="#6c63ff"
          borderRadius="50px"
          _hover={{
            bg: "rgba(108, 99, 255, 0.1)",
            transform: "translateY(-2px)"
          }}
          transition="all 0.3s ease"
          leftIcon={<span>+</span>}
        >
          Add Payment Method
        </Button>
      )}
    </Box>
  );
};

export default PaymentMethodManager; 